import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Card, Button, Table, Form, Modal, InputGroup, Alert } from 'react-bootstrap';
import { datasetService, pixelService } from '../../services/api';
import './Datasets.css';

const DatasetList = () => {
  const [datasets, setDatasets] = useState([]);
  const [pixels, setPixels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingDataset, setEditingDataset] = useState(null);
  const [formData, setFormData] = useState({
    nome: '',
    pixelIds: [],
    botToken: '',
    botUsername: '',
    channelId: '',
    groupLink: '',
    maxEntriesPerDay: 100,
    botMessage: 'Olá! Clique no botão abaixo para entrar no grupo.',
    botButtonText: 'Entrar no Grupo'
  });
  const [error, setError] = useState('');
  const [validatingToken, setValidatingToken] = useState(false);
  const [tokenValidated, setTokenValidated] = useState(false);
  const [checkingChannel, setCheckingChannel] = useState(false);
  const [channelChecked, setChannelChecked] = useState(false);
  const [channelTitle, setChannelTitle] = useState('');
  const [generatedScript, setGeneratedScript] = useState(null);

  useEffect(() => {
    fetchDatasets();
    fetchPixels();
  }, []);

  const fetchDatasets = async () => {
    setLoading(true);
    setError('');
    
    try {
      const response = await datasetService.getAll();
      const datasetsArray = Array.isArray(response?.data) ? response.data : [];
      setDatasets(datasetsArray);
    } catch (error) {
      console.error('Erro ao carregar datasets:', error);
      setError('Falha ao carregar datasets. Por favor, tente novamente.');
      setDatasets([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchPixels = async () => {
    try {
      const response = await pixelService.getAll();
      const pixelsArray = Array.isArray(response?.data) ? response.data : [];
      setPixels(pixelsArray);
    } catch (error) {
      console.error('Erro ao carregar pixels:', error);
      setError('Falha ao carregar pixels. Por favor, tente novamente.');
      setPixels([]);
    }
  };

  // Filtrar datasets com base na pesquisa
  const filteredDatasets = datasets.filter(dataset => 
    dataset?.nome?.toLowerCase().includes(searchTerm.toLowerCase()) ||
    dataset?.channelId?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Abrir modal para adicionar novo dataset
  const handleAddDataset = () => {
    setEditingDataset(null);
    setFormData({
      nome: '',
      pixelIds: [],
      botToken: '',
      botUsername: '',
      channelId: '',
      groupLink: '',
      maxEntriesPerDay: 100,
      botMessage: 'Olá! Clique no botão abaixo para entrar no grupo.',
      botButtonText: 'Entrar no Grupo'
    });
    setShowModal(true);
    setTokenValidated(false);
    setChannelChecked(false);
    setGeneratedScript(null);
    setError('');
  };

  // Abrir modal para editar dataset existente
  const handleEditDataset = async (dataset) => {
    setEditingDataset(dataset);
    setFormData({
      nome: dataset.nome,
      pixelIds: dataset.pixelIds.map(pixel => pixel._id || pixel),
      botToken: dataset.botToken,
      botUsername: dataset.botUsername,
      channelId: dataset.channelId,
      groupLink: dataset.groupLink,
      maxEntriesPerDay: dataset.maxEntriesPerDay,
      botMessage: dataset.botMessage,
      botButtonText: dataset.botButtonText
    });
    setShowModal(true);
    setTokenValidated(true);
    setChannelChecked(true);
    setError('');
    
    try {
      // Buscar o script gerado
      const scriptResponse = await datasetService.getScript(dataset._id);
      setGeneratedScript(scriptResponse.data);
    } catch (error) {
      console.error('Erro ao buscar script:', error);
      setError('Falha ao carregar o script. Por favor, tente novamente.');
    }
  };

  // Fechar modal
  const handleCloseModal = () => {
    setShowModal(false);
    setError('');
  };

  // Atualizar dados do formulário
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  // Alternar seleção de pixel
  const togglePixelSelection = (pixelId) => {
    const pixelIds = [...formData.pixelIds];
    const pixel = pixels.find(p => p._id === pixelId);
    
    if (!pixel) {
      return;
    }
    
    if (pixelIds.includes(pixelId)) {
      // Remover pixel
      const index = pixelIds.indexOf(pixelId);
      pixelIds.splice(index, 1);
    } else {
      // Adicionar pixel
      pixelIds.push(pixelId);
    }
    
    setFormData(prev => ({ ...prev, pixelIds }));
  };

  // Renderizar lista de pixels
  const renderPixelList = (isSelected) => {
    if (!Array.isArray(pixels) || pixels.length === 0) {
      return <div className="text-center p-3">Nenhum pixel encontrado</div>;
    }

    const filteredPixels = pixels.filter(pixel => {
      const isPixelSelected = formData.pixelIds.includes(pixel._id);
      return isSelected ? isPixelSelected : !isPixelSelected;
    });

    if (filteredPixels.length === 0) {
      return (
        <div className="text-center p-3">
          {isSelected ? 'Nenhum pixel selecionado' : 'Todos os pixels já foram selecionados'}
        </div>
      );
    }

    return filteredPixels.map(pixel => (
      <div
        key={pixel._id}
        className={`pixel-item ${isSelected ? 'selected' : ''}`}
        onClick={() => togglePixelSelection(pixel._id)}
      >
        <div className="pixel-name">{pixel.nome}</div>
        <div className="pixel-info">
          ID: {pixel.pixelId}
          <br />
          Domínio: {pixel.domainId?.url || pixel.domainId}
        </div>
      </div>
    ));
  };

  // Validar token do bot
  const handleValidateToken = async () => {
    if (!formData.botToken) {
      setError('Token do bot é obrigatório');
      return;
    }

    setValidatingToken(true);
    setTokenValidated(false);
    setError('');
    
    try {
      const response = await datasetService.validateBotToken(formData.botToken);
      
      if (response.data.success) {
        const botInfo = response.data.botInfo;
        setFormData(prev => ({
          ...prev,
          botUsername: botInfo.username
        }));
        setTokenValidated(true);
        setError('');
      } else {
        throw new Error(response.data.error || 'Token inválido');
      }
    } catch (error) {
      console.error('Erro ao validar token:', error);
      setError(error.response?.data?.error || error.message || 'Falha ao validar token do bot. Verifique se o token está correto.');
      setTokenValidated(false);
    } finally {
      setValidatingToken(false);
    }
  };

  // Verificar canal
  const handleCheckChannel = async () => {
    if (!formData.botToken || !formData.channelId) {
      setError('Token do bot e ID do canal são obrigatórios');
      return;
    }

    setCheckingChannel(true);
    setChannelChecked(false);
    setError('');
    
    try {
      const response = await datasetService.checkChannel({
        botToken: formData.botToken,
        channelId: formData.channelId
      });

      if (response.data.success) {
        const channelInfo = response.data.channelInfo;
        setFormData(prev => ({
          ...prev,
          channelId: channelInfo.id
        }));
        setChannelChecked(true);
        setChannelTitle(channelInfo.title);
        setError('');
      } else {
        throw new Error(response.data.error || 'Canal não encontrado ou bot sem acesso');
      }
    } catch (error) {
      console.error('Erro ao verificar canal:', error);
      setError(error.response?.data?.error || error.message || 'Falha ao verificar canal. Verifique se o bot está no canal e tem permissões adequadas.');
      setChannelChecked(false);
    } finally {
      setCheckingChannel(false);
    }
  };

  // Salvar dataset (criar ou atualizar)
  const handleSaveDataset = async () => {
    if (!formData.nome || !tokenValidated || !channelChecked || formData.pixelIds.length === 0) {
      setError('Todos os campos são obrigatórios e o token e canal devem ser validados');
      return;
    }

    try {
      let savedDataset;
      
      if (editingDataset) {
        const response = await datasetService.update(editingDataset._id, formData);
        savedDataset = response.data.data;
      } else {
        const response = await datasetService.create(formData);
        savedDataset = response.data.data;
      }

      if (savedDataset && savedDataset._id) {
        // Buscar o script gerado
        const scriptResponse = await datasetService.getScript(savedDataset._id);
        setGeneratedScript(scriptResponse.data);
        
        // Recarregar lista de datasets
        fetchDatasets();
        
        // Não fechar o modal para mostrar o script gerado
        setEditingDataset(savedDataset);
      } else {
        throw new Error('Resposta inválida do servidor');
      }
    } catch (error) {
      console.error('Erro ao salvar dataset:', error);
      setError(error.response?.data?.message || error.message || 'Falha ao salvar dataset. Por favor, tente novamente.');
    }
  };

  // Excluir dataset
  const handleDeleteDataset = async (datasetId) => {
    if (window.confirm('Tem certeza que deseja excluir este dataset?')) {
      try {
        await datasetService.delete(datasetId);
        fetchDatasets();
      } catch (error) {
        console.error('Erro ao excluir dataset:', error);
        alert('Falha ao excluir dataset. Por favor, tente novamente.');
      }
    }
  };

  // Obter nome do pixel pelo ID
  const getPixelName = (pixelId) => {
    const pixel = pixels.find(p => p._id === pixelId);
    return pixel ? pixel.nome : 'Pixel não encontrado';
  };

  return (
    <Container fluid className="datasets-container">
      <h1 className="mb-4">Datasets</h1>
      
      {error && (
        <Alert variant="danger" className="mb-4">
          {error}
        </Alert>
      )}
      
      <Row className="mb-4">
        <Col md={6}>
          <InputGroup>
            <Form.Control
              placeholder="Pesquisar datasets..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </InputGroup>
        </Col>
        <Col md={6} className="text-end">
          <Button variant="primary" onClick={handleAddDataset}>
            Adicionar Dataset
          </Button>
        </Col>
      </Row>

      <Card>
        <Card.Body>
          {loading ? (
            <div className="text-center py-5">
              <div className="spinner-border text-primary" role="status">
                <span className="visually-hidden">Carregando...</span>
              </div>
            </div>
          ) : !Array.isArray(datasets) || datasets.length === 0 ? (
            <div className="text-center p-5">Nenhum dataset cadastrado</div>
          ) : (
            <Table responsive hover>
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Nome</th>
                  <th>Canal</th>
                  <th>Data de Criação</th>
                  <th>Ações</th>
                </tr>
              </thead>
              <tbody>
                {filteredDatasets.map(dataset => (
                  <tr key={dataset._id}>
                    <td>{dataset._id}</td>
                    <td>{dataset.nome}</td>
                    <td>{dataset.channelId}</td>
                    <td>{dataset.createdAt ? new Date(dataset.createdAt).toLocaleDateString() : 'N/A'}</td>
                    <td>
                      <Button 
                        variant="outline-primary" 
                        size="sm" 
                        className="me-2"
                        onClick={() => handleEditDataset(dataset)}
                      >
                        Editar
                      </Button>
                      <Button 
                        variant="outline-danger" 
                        size="sm"
                        onClick={() => handleDeleteDataset(dataset._id)}
                      >
                        Excluir
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </Table>
          )}
        </Card.Body>
      </Card>

      {/* Modal para adicionar/editar dataset */}
      <Modal show={showModal} onHide={handleCloseModal} size="lg">
        <Modal.Header closeButton>
          <Modal.Title>
            {editingDataset ? 'Editar Dataset' : 'Adicionar Dataset'}
          </Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {error && <Alert variant="danger">{error}</Alert>}
          <Form>
            <Form.Group className="mb-3">
              <Form.Label>Nome do Dataset</Form.Label>
              <Form.Control
                type="text"
                name="nome"
                value={formData.nome}
                onChange={handleInputChange}
                placeholder="Nome do dataset"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Pixels</Form.Label>
              <div className="pixels-container">
                <Row>
                  <Col md={6}>
                    <h6>Pixels Disponíveis ({pixels.filter(p => !formData.pixelIds.includes(p._id)).length})</h6>
                    <div className="pixel-list">
                      {renderPixelList(false)}
                    </div>
                  </Col>
                  <Col md={6}>
                    <h6>Pixels Selecionados ({formData.pixelIds.length})</h6>
                    <div className="pixel-list">
                      {renderPixelList(true)}
                    </div>
                  </Col>
                </Row>
              </div>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Token do Bot</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="botToken"
                  value={formData.botToken}
                  onChange={handleInputChange}
                  placeholder="Token do bot do Telegram"
                />
                <Button 
                  variant="outline-secondary"
                  onClick={handleValidateToken}
                  disabled={validatingToken}
                >
                  {validatingToken ? 'Validando...' : 'Validar Token'}
                </Button>
              </InputGroup>
              {tokenValidated && (
                <div className="validation-status success">
                  ✓ Token válido - Bot: @{formData.botUsername}
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>ID do Canal</Form.Label>
              <InputGroup>
                <Form.Control
                  type="text"
                  name="channelId"
                  value={formData.channelId}
                  onChange={handleInputChange}
                  placeholder="ID do canal do Telegram"
                  disabled={!tokenValidated}
                />
                <Button 
                  variant="outline-secondary"
                  onClick={handleCheckChannel}
                  disabled={checkingChannel || !tokenValidated}
                >
                  {checkingChannel ? 'Verificando...' : 'Verificar Canal'}
                </Button>
              </InputGroup>
              {channelChecked && (
                <div className="validation-status success">
                  ✓ Canal "{channelTitle}" verificado com sucesso
                </div>
              )}
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Link do Grupo</Form.Label>
              <Form.Control
                type="text"
                name="groupLink"
                value={formData.groupLink}
                onChange={handleInputChange}
                placeholder="Link do grupo do Telegram"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Máximo de Entradas por Dia</Form.Label>
              <Form.Control
                type="number"
                name="maxEntriesPerDay"
                value={formData.maxEntriesPerDay}
                onChange={handleInputChange}
                placeholder="Limite diário de entradas"
                min="1"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Mensagem do Bot</Form.Label>
              <Form.Control
                as="textarea"
                rows={3}
                name="botMessage"
                value={formData.botMessage}
                onChange={handleInputChange}
                placeholder="Mensagem que o bot enviará"
              />
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>Texto do Botão</Form.Label>
              <Form.Control
                type="text"
                name="botButtonText"
                value={formData.botButtonText}
                onChange={handleInputChange}
                placeholder="Texto do botão com o link"
              />
            </Form.Group>

            {generatedScript && (
              <div className="mt-4">
                <h5>Script de Integração</h5>
                <Alert variant="info">
                  <p><strong>Instruções:</strong></p>
                  <pre className="mb-3">{generatedScript.instructions}</pre>
                  
                  <p><strong>Script:</strong></p>
                  <div className="script-container bg-light p-3 rounded">
                    <pre className="mb-0"><code>{generatedScript.script}</code></pre>
                  </div>
                  
                  <Button 
                    variant="outline-primary" 
                    size="sm" 
                    className="mt-3"
                    onClick={() => {
                      navigator.clipboard.writeText(generatedScript.script);
                      alert('Script copiado para a área de transferência!');
                    }}
                  >
                    Copiar Script
                  </Button>
                </Alert>
              </div>
            )}
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseModal}>
            Cancelar
          </Button>
          <Button 
            variant="primary" 
            onClick={handleSaveDataset}
            disabled={!formData.nome || !tokenValidated || !channelChecked || formData.pixelIds.length === 0}
          >
            Salvar
          </Button>
        </Modal.Footer>
      </Modal>
    </Container>
  );
};

export default DatasetList; 